import { colors } from '@kmns-internal/furikake/tokens'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import {
  TemplateLayoutTypeEnum,
  TemplateNodeTypeEnum,
  type TemplateNodeSchema,
} from '@ulysses-inc/harami_api_client'
import React, { useRef } from 'react'
import { useDrop } from 'react-dnd'
import { shallowEqual, useSelector } from 'react-redux'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import {
  isUnderEmployeeCheckSection,
  shouldBlockTableMoveIntoEmployeeCheck,
} from 'src/features/templateEdit/section/table/model'
import type { RootState } from 'src/state/store'
import { ERROR_MESSAGES } from '../common/errorMessages'
import { useErrorDialog } from '../common/useErrorDialog'
import type { DragItem, MoveNodeEvent } from '../dragAndDrop/DnDTemplateType'
import { useAddTableSection } from './useAddTableSection'

interface OwnProps {
  node: TemplateNodeSchema | null
  parentNode: TemplateNodeSchema | null
  addTemplateNode: (
    targetNode: TemplateNodeSchema | null,
    parentNode: TemplateNodeSchema | null,
    position: 'top' | 'bottom',
    addTemplateNodeType: TemplateNodeTypeEnum,
  ) => void
  moveNode: (event: MoveNodeEvent) => void
}

// 一つも質問がないセクション、ロジック配下に質問を移動するためのコンポーネント
// セクション、ロジック直下の「質問を追加・セクションを追加」ボタンに使用されている
// セクション直下の場合には「表を追加」ボタンが表示される
// それ以外の「質問を追加・セクションを追加」ボタンは AddTemplateModifyButtonGroup が使用されている
// https://github.com/Ulysses-inc/harami_client/pull/2390
const AddTemplateModifyButtonGroupInEmpty: React.FC<OwnProps> = (
  props: OwnProps,
) => {
  const { addTableSection, confirmDialog } = useAddTableSection()
  const { showError, errorDialog: moveErrorDialog } = useErrorDialog()
  const { isFeatureAvailable } = useLimitedFeatureAvailability()
  const { layoutType } = useSelector(
    (state: RootState) => state.templatesState.editTemplate,
    shallowEqual,
  )
  const { templateNodes, activePageId, templatePages } = useSelector(
    (state: RootState) => state.templatesState.templatePages,
    shallowEqual,
  )
  const isGridLayout = layoutType === TemplateLayoutTypeEnum.Grid

  const handleAddTableSection = () => {
    addTableSection({
      templateNodes,
      targetNode: props.node,
      parentNode: props.parentNode,
      position: 'top',
      pageNodeIds:
        props.parentNode === null
          ? templatePages[activePageId]?.nodes
          : undefined,
    })
  }

  const ref = useRef<HTMLDivElement>(null)

  const [{ isOver }, drop] = useDrop({
    accept: 'NODE',
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    drop: (dragingItem: DragItem) => {
      const dragNode = dragingItem.node

      if (!isOver) {
        return
      }

      // 表（またはそれを含むセクション）を従業員チェック内に移動させない
      if (
        shouldBlockTableMoveIntoEmployeeCheck(
          dragNode,
          props.parentNode,
          templateNodes,
        )
      ) {
        showError(
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.title,
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.message,
        )
        return
      }

      const event: MoveNodeEvent = {
        dragNode: dragNode,
        dropNode: props.node,
        dropPosition: 'TOP',
        dragNodeParent: dragingItem.parentNode,
        dropNodeParent: props.parentNode,
      }
      props.moveNode(event)
    },
  })

  // eslint-disable-next-line react-hooks/refs -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
  drop(ref)

  return (
    <div ref={ref}>
      {isOver && (
        <Box
          sx={{ height: 46, width: '100%', bgcolor: colors.primary.light }}
        />
      )}
      <Stack direction="row" spacing={1.25} sx={{ m: 1.25 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AddOutlinedIcon />}
          onMouseDown={() => {
            props.addTemplateNode(
              props.node,
              props.parentNode,
              'top',
              TemplateNodeTypeEnum.Question,
            )
          }}
          sx={{ flex: 1 }}
        >
          質問を追加
        </Button>
        {!isGridLayout && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddOutlinedIcon />}
            onMouseDown={() => {
              props.addTemplateNode(
                props.node,
                props.parentNode,
                'top',
                TemplateNodeTypeEnum.Section,
              )
            }}
            sx={{ flex: 1 }}
          >
            セクションを追加
          </Button>
        )}
        {/* 従業員チェック配下の判定は重いので、Grid/プランの安価な判定を先に短絡評価する */}
        {!isGridLayout &&
          isFeatureAvailable('tableSection') &&
          !isUnderEmployeeCheckSection(props.parentNode, templateNodes) && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AddOutlinedIcon />}
              onMouseDown={handleAddTableSection}
              sx={{ flex: 1 }}
            >
              表を追加
            </Button>
          )}
      </Stack>
      {confirmDialog}
      {moveErrorDialog}
    </div>
  )
}

export default AddTemplateModifyButtonGroupInEmpty
